import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  Button,
  Center,
  Container,
  Flex,
  Group,
  Loader,
  LoadingOverlay,
  Modal,
  ScrollArea,
  Stack,
  Tabs,
  Text,
  Title,
  px,
  useMantineTheme,
} from '@mantine/core';
import { useDebouncedValue, useDisclosure, useMediaQuery } from '@mantine/hooks';
import { IconChevronLeft, IconChevronRight, IconClipboardText, IconLogin } from '@tabler/icons-react';
import KiloButton from 'components/KiloButton';
import KiloInput from 'components/KiloInput';
import KiloTabs from 'components/KiloTabs/KiloTabs';
import LastCreditAlertModal from 'components/LastCreditAlertModal';
import { useAuth } from 'context/auth-context';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import useAppointment from 'hooks/appointment/useAppointment';
import useEligibleAthletes from 'hooks/athlete/useEligibleAthletes';
import useCreateAthleteAppointment from 'hooks/athleteAppointment/useCreateAthleteAppointment';
import useUpdateAthleteAppointment from 'hooks/athleteAppointment/useUpdateAthleteAppointment';
import useCreateAthleteClass from 'hooks/athleteClass/useCreateAthleteClass';
import useUpdateAthleteClass from 'hooks/athleteClass/useUpdateAthleteClass';
import useAthletePackageAllLimitedCreditsUsed from 'hooks/athletePackage/useAthletePackageAllLimitedCreditsUsed';
import useLastCreditAlertBuilder from 'hooks/athletePackage/useLastCreditAlertBuilder';
import useClass from 'hooks/class/useClass';
import useKioskEvent from 'hooks/event/useKioskEvent';
import { useStaffTracking } from 'hooks/mixpanel/useStaffTracking';
import { useQueryClient } from 'react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  AthleteAppointmentEventRow,
  AthleteAppointmentWithAthlete,
  AthleteAppointmentWrite,
  AthleteKiloClassKioskRosterRow,
  AthleteKiloClassWithAthleteAndWaitlistOrder,
  AthleteKiloClassWrite,
  AthleteNote,
  EligibleAthlete,
} from 'types/generated';
import { getFormattedCoachesNames } from 'utils/event.helpers';
import { formatDatesForEvent, getStartsIn } from 'utils/formatters';
import { LastCreditAlertContent } from 'utils/last-credit-alert';
import notificationHandler from 'utils/notifications';
import { isWorkoutTrackerEnabled } from 'utils/workoutTracker';
import { v4 as uuidv4 } from 'uuid';

import EventDetailCard from './components/EventDetailCard';
import EventNotesContentModal from './components/EventNotesContentModal';
import EnterKioskModeModal from '../Kiosk/components/EnterKioskModeModal';

dayjs.extend(isBetween);

const KIOSK_LAST_CREDIT_ALERT_TIMEOUT_MS = 8000;

type StartsInStatus = 'ongoing' | 'upcoming' | 'ended';
type StartsInStatusesColors = {
  text: string;
  background: string;
  border: string;
  borderWidth: string;
};

/** `EligibleAthlete` plus the two roster indicators. Optional because the Eligible tab's rows
 * come straight from the eligible-athletes endpoint and carry neither. */
export type KioskRosterAthlete = EligibleAthlete & {
  isFirstClassVisit?: boolean;
  athleteStatus?: string | null;
};

type AthleteNoteWithAthleteStatuses = AthleteNote & {
  // Optional on purpose: only the class branch's rows carry these. Appointment rows come from
  // AthleteAppointmentEventRow, which has neither, and the Eligible tab has neither either.
  athleteStatus?: string | null;
  isReserved: boolean;
  isCheckedIn: boolean;
  hasPastDueBills: boolean;
  hasUnsignedDocuments: boolean;
  // Lives on the reservation row, not the athlete, so it has to be lifted here alongside the
  // other per-reservation flags. Appointments never carry it -- First Class Visit is class-scoped.
  isFirstClassVisit: boolean;
};

const KioskDetail = (): JSX.Element => {
  const theme = useMantineTheme();
  const { session } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const containerRef = useRef<HTMLDivElement>(null);
  const { eventId } = useParams<{ eventId: string }>();
  const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.sm}`, true);
  const isKioskMode = localStorage.getItem('isKiosk') === 'true';

  const eventType = useMemo(() => {
    return searchParams.get('eventType') as 'class' | 'appointment';
  }, [searchParams]);
  const redirectedFrom = useMemo(() => {
    return searchParams.get('from') as 'calendar' | 'kiosk';
  }, [searchParams]);

  const [isEnterKioskModalOpened, { open: openKioskModal, close: closeKioskModal }] = useDisclosure(false);
  const [isNotesModalOpen, { open: openNotesModal, close: closeNotesModal }] = useDisclosure(false);
  const { successNotification, errorNotification } = notificationHandler();
  const staffTracking = useStaffTracking();

  const [athletes, setAthletes] = useState<AthleteNoteWithAthleteStatuses[]>([]);
  const [filteredAthletes, setFilteredAthletes] = useState<AthleteNoteWithAthleteStatuses[]>([]);
  const [eligibleAthletes, setEligibleAthletes] = useState<EligibleAthlete[]>([]);
  const [filteredEligibleAthletes, setFilteredEligibleAthletes] = useState<EligibleAthlete[]>([]);
  const [loadingAthleteId, setLoadingAthleteId] = useState<string | null>(null);

  const isAthleteEventsLoading = false;
  const [activeTab, setActiveTab] = useState<string>('isReserved');
  const [search, setSearch] = useState<string>('');
  const [debouncedSearch] = useDebouncedValue(search, 250);

  const { data: selectedEventData, isLoading } = useKioskEvent({
    eventId: eventId || '',
    eventType: eventType,
    refetchInterval: 60000,
  });

  const { data, fetchNextPage, isFetchingNextPage } = useEligibleAthletes({
    selectedEvent: selectedEventData?.data,
    search: debouncedSearch,
    enabled: activeTab === 'eligible',
  });

  const workoutTrackerEnabled = isWorkoutTrackerEnabled(session?.gym?.workoutTrackerEnabled);

  const showViewWorkoutButton = workoutTrackerEnabled && eventType === 'class';

  const { data: classData, isLoading: isClassDataLoading } = useClass(
    selectedEventData?.data?.athleteKiloClasses[0]?.kiloClass,
  );
  const { data: appointmentData, isLoading: isAppointmentDataLoading } = useAppointment(
    selectedEventData?.data.athleteAppointments[0]?.appointment,
  );

  const queryClient = useQueryClient();
  const createAthleteClassMutation = useCreateAthleteClass();
  const updateAthleteClassMutation = useUpdateAthleteClass();
  const createAthleteAppointmentMutation = useCreateAthleteAppointment();
  const updateAthleteAppointmentMutation = useUpdateAthleteAppointment();
  const getAthletePackageAllLimitedCreditsUsed = useAthletePackageAllLimitedCreditsUsed();
  const buildLastCreditAlert = useLastCreditAlertBuilder('athlete');

  const [lastCreditAlert, setLastCreditAlert] = useState<LastCreditAlertContent | null>(null);

  const compressAthlete = (athlete: AthleteNoteWithAthleteStatuses): KioskRosterAthlete => {
    const {
      fullName,
      firstName,
      profilePicture,
      athleteId,
      notes,
      isCheckedIn,
      isReserved,
      hasPastDueBills,
      hasUnsignedDocuments,
      isFirstClassVisit,
      athleteStatus,
    } = athlete;

    return {
      fullName,
      firstName,
      profilePicture,
      athleteId,
      isCheckedIn,
      isReserved,
      notes,
      hasPastDueBills,
      hasUnsignedDocuments,
      isFirstClassVisit,
      athleteStatus,
    };
  };

  const selectedEvent = useMemo(() => {
    return selectedEventData?.data;
  }, [selectedEventData]);

  const isAppointment = useMemo(() => {
    return eventType === 'appointment';
  }, [eventType]);

  const isActiveTabEligible = useMemo(() => {
    return activeTab === 'eligible';
  }, [activeTab]);

  const isClassFull = useMemo(() => {
    if (selectedEvent?.capacity == null || selectedEvent.eventType !== 'kilo_class_type') return false;
    return selectedEvent.isAtFullCapacity ?? false;
  }, [selectedEvent]);

  const searchAthletes = useCallback(
    (athletesFilteredByStatus: KioskRosterAthlete[]): KioskRosterAthlete[] | [] => {
      if (search === '') {
        return athletes;
      }

      return athletesFilteredByStatus.filter(athlete =>
        athlete.fullName.toLowerCase().includes(search.toLowerCase()),
      );
    },
    [athletes, search],
  );

  const athletesToRender = useMemo(() => {
    return isActiveTabEligible
      ? filteredEligibleAthletes
      : searchAthletes(filteredAthletes.map(compressAthlete));
  }, [filteredAthletes, filteredEligibleAthletes, isActiveTabEligible, searchAthletes]);

  const getTextFromActiveTab = (): string => {
    switch (activeTab) {
      case 'isReserved':
        return 'reserved';
      case 'isCheckedIn':
        return 'checked in';
      case 'eligible':
        return 'eligible';
      default:
        return '';
    }
  };

  const getReservedAndCheckedInAthletesFromEvents = useCallback(
    (
      event: AthleteAppointmentEventRow[] | AthleteKiloClassKioskRosterRow[],
      status: 'isReserved' | 'isCheckedIn',
    ): AthleteNoteWithAthleteStatuses[] => {
      const filteredByTab =
        activeTab === 'isReserved'
          ? event.filter(
              athleteKiloClassOrAppointment =>
                athleteKiloClassOrAppointment.isReserved && !athleteKiloClassOrAppointment.isCheckedIn,
            )
          : event;

      return filteredByTab
        .filter(
          athleteKiloClassOrAppointment =>
            athleteKiloClassOrAppointment[status] &&
            !athleteKiloClassOrAppointment.isLateCancel &&
            !athleteKiloClassOrAppointment.isNoShow,
        )
        .map(athleteKiloClassOrAppointment => {
          const athlete = athleteKiloClassOrAppointment.athlete;
          return {
            ...athlete,
            isReserved: athleteKiloClassOrAppointment.isReserved,
            isCheckedIn: athleteKiloClassOrAppointment.isCheckedIn,
            hasPastDueBills: athleteKiloClassOrAppointment.hasPastDueBills,
            hasUnsignedDocuments: athleteKiloClassOrAppointment.hasUnsignedDocuments,
            isFirstClassVisit:
              'isFirstClassVisit' in athleteKiloClassOrAppointment
                ? athleteKiloClassOrAppointment.isFirstClassVisit
                : false,
          };
        });
    },
    [activeTab],
  );

  useEffect(() => {
    if (selectedEvent) {
      const filterAthletesByAthleteStatus = (
        status: 'isReserved' | 'isCheckedIn',
      ): Array<AthleteNoteWithAthleteStatuses> => {
        switch (selectedEvent.eventType) {
          case 'appointment_type':
            return getReservedAndCheckedInAthletesFromEvents(selectedEvent.athleteAppointments, status);

          case 'kilo_class_type':
            return getReservedAndCheckedInAthletesFromEvents(selectedEvent.athleteKiloClasses, status);
          default:
            return [];
        }
      };

      const getFilteredAthletes = (): AthleteNoteWithAthleteStatuses[] => {
        switch (activeTab) {
          case 'isCheckedIn':
            return filterAthletesByAthleteStatus('isCheckedIn');
          case 'isReserved':
            return filterAthletesByAthleteStatus('isReserved');
          default:
            return [];
        }
      };

      setAthletes(getFilteredAthletes());
      setFilteredAthletes(getFilteredAthletes());

      if (activeTab === 'eligible') {
        const flattenedEligibleAthletes = data?.pages.flatMap(page => page.data.results) ?? [];
        setEligibleAthletes(flattenedEligibleAthletes);
        setFilteredEligibleAthletes(flattenedEligibleAthletes);
      }
    } else {
      setAthletes([]);
    }
  }, [selectedEvent, activeTab, data, getReservedAndCheckedInAthletesFromEvents]);

  const goBack = (): void => {
    if (redirectedFrom === 'calendar') {
      var selectedKiloClassOrAppointmentID;
      if (eventType === 'class') {
        selectedKiloClassOrAppointmentID = selectedEventData?.data?.athleteKiloClasses[0]?.kiloClass;
      } else if (eventType === 'appointment') {
        selectedKiloClassOrAppointmentID = selectedEventData?.data.athleteAppointments[0]?.appointment;
      }

      navigate(`/calendar/scheduling?eventId=${selectedKiloClassOrAppointmentID}&eventType=${eventType}`);
      return;
    }

    if (isKioskMode) {
      navigate(`/?eventType=${eventType}`);
    } else {
      navigate(`/kiosk?eventType=${eventType}`);
    }
  };

  const closeLastCreditAlert = (): void => {
    setLastCreditAlert(null);
    if (isKioskMode) goBack();
  };

  const closeLastCreditAlertRef = useRef(closeLastCreditAlert);
  closeLastCreditAlertRef.current = closeLastCreditAlert;

  /** Closes the last-credit alert after a timeout in kiosk mode. */
  useEffect(() => {
    if (!isKioskMode || !lastCreditAlert) return;

    const timer = setTimeout(() => closeLastCreditAlertRef.current(), KIOSK_LAST_CREDIT_ALERT_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [isKioskMode, lastCreditAlert]);

  /** Shows the last-credit alert when the check-in used it; in kiosk mode, leaves once nothing is shown. */
  const finishCheckIn = (
    isCheckedIn: boolean,
    creditDispensation: string | null | undefined,
    firstName: EligibleAthlete['firstName'],
  ): void => {
    if (!isCheckedIn || !creditDispensation) {
      if (isKioskMode) goBack();
      return;
    }

    getAthletePackageAllLimitedCreditsUsed.mutate(
      { creditDispensation },
      {
        onSuccess: res => {
          const alert = buildLastCreditAlert(res.data, firstName);
          if (alert) {
            setLastCreditAlert(alert);
          } else if (isKioskMode) {
            goBack();
          }
        },
        onError: error => {
          errorNotification('Error to check if all limited credits used', error.response?.data.message);
          if (isKioskMode) goBack();
        },
      },
    );
  };

  const handleCheckInAthleteClass = (
    selectedAthleteClass: AthleteKiloClassWithAthleteAndWaitlistOrder | undefined,
    athlete: EligibleAthlete,
  ): void => {
    const payload: AthleteKiloClassWrite = {
      athleteKiloClassId: !!selectedAthleteClass ? selectedAthleteClass.athleteKiloClassId : uuidv4(),
      isReserved: !!selectedAthleteClass?.isReserved,
      isCheckedIn: !athlete.isCheckedIn,
      isWaitlisted: false,
      kiloClass: eventId!,
      athlete: athlete.athleteId,
      registrationDatetime: !!selectedAthleteClass
        ? selectedAthleteClass.registrationDatetime
        : dayjs(new Date()).format('YYYY-MM-DDTHH:mm:ss'),
      isKioskSelfService: isKioskMode,
    };

    if (!!selectedAthleteClass) {
      setLoadingAthleteId(athlete.athleteId);
      updateAthleteClassMutation.mutate(payload, {
        onSuccess: response => {
          setLoadingAthleteId(null);
          setSearch('');

          // Track kiosk check-in
          if (eventId && selectedEvent?.eventName && payload.isCheckedIn) {
            staffTracking.trackKioskCheckin(
              athlete.athleteId,
              athlete.fullName,
              eventId,
              selectedEvent.eventName,
              !isKioskMode ? 'staff_assisted' : 'athlete_self_service',
            );
          }

          finishCheckIn(payload.isCheckedIn, response.data?.creditDispensation, athlete.firstName);

          successNotification(
            'Adding athlete to class success',
            `${athlete.fullName} has been successfully added to ${selectedEvent?.eventName}.`,
          );
        },
        onError: error => {
          setLoadingAthleteId(null);
          errorNotification('Adding athlete failed', error.response?.data.message);
        },
      });
    } else {
      setLoadingAthleteId(athlete.athleteId);
      createAthleteClassMutation.mutate(payload, {
        onSuccess: response => {
          setLoadingAthleteId(null);
          setSearch('');

          // Track kiosk check-in
          if (eventId && selectedEvent?.eventName && payload.isCheckedIn) {
            staffTracking.trackKioskCheckin(
              athlete.athleteId,
              athlete.fullName,
              eventId,
              selectedEvent.eventName,
              !isKioskMode ? 'staff_assisted' : 'athlete_self_service',
            );
          }

          finishCheckIn(payload.isCheckedIn, response.data?.creditDispensation, athlete.firstName);

          successNotification(
            'Adding athlete to class success',
            `${athlete.fullName} has been successfully added to ${selectedEvent?.eventName}.`,
          );
        },
        onError: error => {
          setLoadingAthleteId(null);
          errorNotification('Adding athlete failed', error.response?.data.message);
          // A full-capacity rejection means our cached class state is stale (e.g. a concurrent
          // check-in took the last slot). Refetch only this event's detail query (prefix-matched,
          // ignoring the trailing refetchInterval key part) so isAtFullCapacity updates and the
          // Check In button disables immediately instead of waiting for the next poll.
          if (error.response?.status === 400) {
            queryClient.invalidateQueries({ queryKey: ['events', 'kiosk-event', eventId, eventType] });
          }
        },
      });
    }
  };
  const handleCheckInAthleteAppointment = (
    selectedAppointment: AthleteAppointmentWithAthlete | undefined,
    athlete: EligibleAthlete,
  ): void => {
    const payload: AthleteAppointmentWrite = {
      athleteAppointmentId: !!selectedAppointment ? selectedAppointment.athleteAppointmentId : uuidv4(),
      isReserved: !!selectedAppointment?.isReserved,
      isCheckedIn: !athlete.isCheckedIn,
      appointment: eventId!,
      athlete: athlete.athleteId,
    };
    if (!!selectedAppointment) {
      setLoadingAthleteId(athlete.athleteId);
      updateAthleteAppointmentMutation.mutate(payload, {
        onSuccess: response => {
          setLoadingAthleteId(null);
          setSearch('');

          // Track kiosk check-in
          if (eventId && selectedEvent?.eventName && payload.isCheckedIn) {
            staffTracking.trackKioskCheckin(
              athlete.athleteId,
              athlete.fullName,
              eventId,
              selectedEvent.eventName,
              !isKioskMode ? 'staff_assisted' : 'athlete_self_service',
            );
          }

          finishCheckIn(payload.isCheckedIn, response.data?.creditDispensation, athlete.firstName);

          successNotification(
            'Creating appointment for athlete success',
            `${athlete.fullName} has been successfully added to ${selectedEvent?.eventName}.`,
          );
        },
        onError: error => {
          setLoadingAthleteId(null);
          errorNotification('Adding athlete failed', error.response?.data.message);
        },
      });
    } else {
      setLoadingAthleteId(athlete.athleteId);
      createAthleteAppointmentMutation.mutate(payload, {
        onSuccess: response => {
          setLoadingAthleteId(null);
          setSearch('');

          // Track kiosk check-in
          if (eventId && selectedEvent?.eventName && payload.isCheckedIn) {
            staffTracking.trackKioskCheckin(
              athlete.athleteId,
              athlete.fullName,
              eventId,
              selectedEvent.eventName,
              !isKioskMode ? 'staff_assisted' : 'athlete_self_service',
            );
          }

          finishCheckIn(payload.isCheckedIn, response.data?.creditDispensation, athlete.firstName);

          successNotification(
            'Creating appointment for athlete success',
            `${athlete.fullName} has been successfully added to ${selectedEvent?.eventName}.`,
          );
        },
        onError: error => {
          setLoadingAthleteId(null);
          errorNotification('Adding athlete failed', error.response?.data.message);
        },
      });
    }
  };

  const handleCheckin = (athlete: EligibleAthlete): void => {
    if (isKioskMode && isActiveTabEligible && !athlete.isReserved && isClassFull) return;

    const selectedAthleteClass = selectedEvent?.athleteKiloClasses?.find(
      athleteKiloClass => athleteKiloClass.athlete.athleteId === athlete.athleteId,
    );
    const selectedAppointment = selectedEvent?.athleteAppointments?.find(
      athleteKiloClass => athleteKiloClass.athlete.athleteId === athlete.athleteId,
    );

    if (isAppointment) {
      handleCheckInAthleteAppointment(selectedAppointment, athlete);
    } else {
      handleCheckInAthleteClass(selectedAthleteClass, athlete);
    }
  };

  const getSelectedAthleteKiloClass = useCallback(
    (athlete: EligibleAthlete) => {
      return selectedEvent?.athleteKiloClasses.filter(akc => akc.athlete.athleteId === athlete.athleteId)[0];
    },
    [selectedEvent],
  );

  const getSelectedAthleteAppointment = useCallback(
    (athlete: EligibleAthlete) => {
      return selectedEvent?.athleteAppointments.filter(
        aap => aap.athlete.athleteId === athlete.athleteId,
      )[0];
    },
    [selectedEvent],
  );

  const checkIfMobileNotesModalIsEmpty = useMemo((): boolean => {
    if (isActiveTabEligible) {
      return eligibleAthletes.some(athlete => athlete.notes?.length);
    }
    return athletes.some(athlete => athlete.notes?.length);
  }, [eligibleAthletes, athletes, isActiveTabEligible]);

  const getNotesToShow = useMemo(() => {
    if (isActiveTabEligible) return eligibleAthletes.filter(athlete => athlete.notes?.length);
    return athletes.filter(athlete => athlete.notes?.length);
  }, [athletes, eligibleAthletes, isActiveTabEligible]);

  const startsInStatus = useCallback((): StartsInStatus => {
    const startsIn = getStartsIn(selectedEvent);
    if (startsIn === 'Ongoing') {
      return 'ongoing';
    } else if (startsIn === 'Ended') {
      return 'ended';
    } else {
      return 'upcoming';
    }
  }, [selectedEvent]);

  const returnStylesForStartsInStatuses = useMemo((): StartsInStatusesColors | undefined => {
    const status = startsInStatus();
    switch (status) {
      case 'ongoing':
        return {
          text: '#61CC75',
          background: '#E2F6E6',
          border: '#61CC75',
          borderWidth: '3px',
        };
      case 'upcoming':
        return {
          text: '#15B8FF',
          background: '#E1F3FE',
          border: 'rgb(222, 226, 230)',
          borderWidth: '0px',
        };

      case 'ended':
        return {
          text: 'black',
          background: 'transparent',
          border: 'rgb(222, 226, 230)',
          borderWidth: '0px',
        };

      default:
        break;
    }
  }, [startsInStatus]);

  if (isLoading || isClassDataLoading || isAppointmentDataLoading) {
    return <LoadingOverlay visible={isLoading} />;
  }

  return (
    <>
      <ScrollArea
        onScrollPositionChange={() => {
          const scrollEl = containerRef.current?.querySelector('.mantine-ScrollArea-viewport');

          if (isFetchingNextPage || !scrollEl || !isActiveTabEligible) {
            return;
          }

          const fullHeight = scrollEl.scrollHeight;
          const visibleHeight = scrollEl.clientHeight;
          const scrollOffset = scrollEl.scrollTop;
          const prefetchOffset = 200;

          if (fullHeight - (scrollOffset + visibleHeight) < prefetchOffset) {
            fetchNextPage();
          }
        }}
        ref={containerRef}
        bg='white'
        h={'100vh'}
      >
        <Container w='100%'>
          <Stack spacing='xl' py='xl'>
            <Group position='apart' align='top' noWrap spacing='md'>
              <Flex direction='column' gap={0}>
                <Group
                  bg={'#15B8FF'}
                  w={48}
                  h={48}
                  mb={32}
                  style={{
                    borderRadius: '50%',
                    justifyContent: 'center',
                    cursor: 'pointer',
                  }}
                  onClick={() => goBack()}
                >
                  <IconChevronLeft color='white' type='default' />
                </Group>
                {isDesktop && (
                  <Text weight={600} size={px(theme.fontSizes.xl) + 1.5} color='dimmed'>
                    {!isKioskMode ? 'Staff' : 'Athlete'} Kiosk
                  </Text>
                )}
                <Group py={isDesktop ? 3 : 15}>
                  <Title
                    order={1}
                    size={isDesktop ? px(theme.spacing.lg) * 2 : px(theme.spacing.sm) * 2}
                    weight={600}
                  >
                    {selectedEvent?.eventName}
                  </Title>
                </Group>

                <Group>
                  <Title order={1} weight={600} size={px(theme.fontSizes.xl)} color='dimmed'>
                    {formatDatesForEvent(selectedEvent)} /{' '}
                    {getFormattedCoachesNames(selectedEvent, isDesktop ? 2 : 1)}
                  </Title>
                  {isDesktop && (
                    <Group
                      bg={returnStylesForStartsInStatuses?.background}
                      py={4}
                      px={18}
                      style={{ borderRadius: '12px', color: returnStylesForStartsInStatuses?.text }}
                      spacing={16.5}
                    >
                      <Text weight={400} size={14}>
                        {getStartsIn(selectedEvent)}
                      </Text>
                    </Group>
                  )}
                </Group>
                {showViewWorkoutButton && (
                  <Group mt={6}>
                    <Button
                      variant='subtle'
                      color='blue'
                      rightIcon={<IconChevronRight size={18} />}
                      onClick={() => navigate(`/kiosk/${eventId}/workout?eventType=${eventType}`)}
                      data-cy='viewWorkoutBtn'
                      sx={{
                        textDecoration: 'underline',
                        padding: 0,
                        height: 'auto',
                        '&:hover': {
                          backgroundColor: 'transparent',
                        },
                      }}
                    >
                      View Workout
                    </Button>
                  </Group>
                )}
              </Flex>

              {!isKioskMode && (
                <Stack align='end' justify='space-between'>
                  <Button
                    leftIcon={isDesktop ? <IconLogin size={theme.fontSizes.xl} /> : null}
                    onClick={openKioskModal}
                    data-cy='enterKioskModeBtn'
                    size={isDesktop ? 'lg' : 'sm'}
                  >
                    Enter Athlete mode
                  </Button>

                  {!isDesktop && (
                    <KiloButton
                      disabled={!checkIfMobileNotesModalIsEmpty}
                      variant='light'
                      size='lg'
                      px={7}
                      py={14}
                      onClick={openNotesModal}
                    >
                      <IconClipboardText size={20} style={{ marginRight: '8px' }} />
                      Pinned Notes
                    </KiloButton>
                  )}
                </Stack>
              )}
            </Group>

            <KiloTabs
              activeTab={activeTab}
              setActiveTab={value => setActiveTab(value)}
              mt={isDesktop ? 16 : 12}
            >
              <>
                <Tabs.Tab value='isReserved'>Reserved</Tabs.Tab>
                <Tabs.Tab value='isCheckedIn'>Checked In</Tabs.Tab>
                {selectedEvent?.eventType !== 'appointment_type' && (
                  <Tabs.Tab value='eligible'>Eligible</Tabs.Tab>
                )}
              </>
            </KiloTabs>

            <Stack spacing={0}>
              <KiloInput
                variantStyle='kiosk'
                label=''
                placeholder='Search for Athlete'
                mb={20}
                onChange={event => setSearch(event.target.value)}
                value={search}
              />

              {isKioskMode && isActiveTabEligible && (
                <Text size='sm' color='dimmed' mb={12}>
                  Athletes without reservations can only check in if slots are available.
                </Text>
              )}

              {isAthleteEventsLoading && (
                <Center p='xl'>
                  <Loader />
                </Center>
              )}

              {!athletesToRender.length && (
                <Center p='xl'>
                  <Text weight={400} size={px(theme.fontSizes.xl)} color='dimmed'>
                    No {getTextFromActiveTab()} athletes for this event
                  </Text>
                </Center>
              )}

              {athletesToRender.map(athlete => (
                <EventDetailCard
                  key={athlete.athleteId}
                  athlete={athlete}
                  onClick={value => handleCheckin(value)}
                  activeTab={activeTab}
                  isKioskMode={isKioskMode}
                  isClassFull={isClassFull}
                  athleteAppointment={getSelectedAthleteAppointment(athlete)}
                  athleteKiloClass={getSelectedAthleteKiloClass(athlete)}
                  kiloClass={classData?.data}
                  appointmentData={appointmentData?.data}
                  athleteAppointmentsLength={selectedEvent?.athleteAppointments.length || 0}
                  setSearch={setSearch}
                  isLoading={loadingAthleteId === athlete.athleteId}
                />
              ))}
            </Stack>
          </Stack>
        </Container>
      </ScrollArea>

      <EnterKioskModeModal opened={isEnterKioskModalOpened} onClose={closeKioskModal} />

      {!isDesktop && (
        <Modal
          opened={isNotesModalOpen}
          onClose={closeNotesModal}
          title={
            <Text weight={700} size={'md'} p={0}>
              Pinned Notes
            </Text>
          }
          centered
          padding='md'
          size={'xl'}
          styles={{
            header: {
              paddingBottom: theme.spacing.sm,
            },
          }}
        >
          {getNotesToShow.map((athlete, index) => {
            return (
              <>
                <Text key={athlete.athleteId} weight={700} size={'md'} pb={'sm'} pt={index === 0 ? 0 : 'md'}>
                  {athlete.fullName}
                </Text>
                <Stack>
                  {athlete.notes?.map(note => (
                    <EventNotesContentModal note={note} key={note.noteId} />
                  ))}
                </Stack>
              </>
            );
          })}
        </Modal>
      )}

      <LastCreditAlertModal alert={lastCreditAlert} onClose={closeLastCreditAlert} />
    </>
  );
};

export default KioskDetail;
